import { logger } from '../../../utils/logger'
import { setJsonSessionItem } from '../../../utils/session-storage'
import type { ServiceHandler } from './service-registry'
import type {
  SetMetadataRequest,
  SetMetadataResponse,
} from '@custom-embedded-code-sdk/methods/metadata'

export const getSessionStorageMetadataKey = (campaignId: string) => `metadata_${campaignId}`

/**
 * SET_METADATA service handler
 *
 * Handles requests to set metadata for the current campaign in session storage.
 * This method overwrites the entire metadata object for the campaign.
 * Sets the session storage key as `metadata_{campaignId}`
 */
export const setMetadata: ServiceHandler<SetMetadataRequest, SetMetadataResponse> = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:setMetadata: Campaign ID not available')
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    if (!payload || typeof payload !== 'object') {
      sendErrorResponse(iframeId, messageId, 'Metadata is expected to be an object')
      return
    }

    const storageKey = getSessionStorageMetadataKey(campaignId)
    setJsonSessionItem(storageKey, payload)

    logger('info', 'CustomEmbeddedCode:setMetadata: Metadata stored successfully', {
      context: { iframeId, messageId, campaignId, payload, storageKey },
    })

    sendSuccessResponse(iframeId, messageId, { success: true })
  } catch (error) {
    logger(
      'error',
      new Error('CustomEmbeddedCode:setMetadata: Failed to store metadata', { cause: error }),
      {
        context: { iframeId, messageId, payload, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to store metadata')
  }
}
